import { faAngleDown, faAngleUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip, TooltipContent, TooltipTrigger } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import { useSearchParams } from 'next/navigation';

interface TableOptionsProps {
  handleSort: (column: string) => void;
  sortConfig: {
    column: string;
    direction: string;
  };
}

export const TableOptions = ({ handleSort, sortConfig }: TableOptionsProps) => {
  const searchParams = useSearchParams();
  const live = searchParams?.get('live') ?? '';

  const getIcon = (column: string) =>
    sortConfig.column === column && sortConfig.direction === 'up' ? faAngleUp : faAngleDown;

  const getTextColorClass = (column: string) =>
    sortConfig.column === column ? 'text-brand-700' : 'text-gray-600';

  return !live ? (
    <div className='container ml-0 flex w-full items-center justify-center p-0 py-3'>
      {/* Rank */}
      <Tooltip placement='bottom'>
        <TooltipTrigger asChild>
          <div
            className={`flex w-[6%] cursor-pointer items-center justify-start gap-x-2 text-xs px-2 ${getTextColorClass('rank')}`}
            onClick={() => handleSort('rank')}
          >
            Rank <FontAwesomeIcon icon={getIcon('rank')} />
          </div>
        </TooltipTrigger>
        <TooltipContent className='w-52 rounded bg-black p-2 text-center text-sm text-white'>
          Player&apos;s ranking based on points earned/dropped in current week.
        </TooltipContent>
      </Tooltip>
      {/* Player */}
      <div
        className={`w-[44%] cursor-pointer ${getTextColorClass('player')}`}
        onClick={() => handleSort('player')}
      >
        <div className='flex items-center justify-start gap-x-2 text-xs'>
          Player <FontAwesomeIcon icon={getIcon('player')} />
        </div>
      </div>
      {/* Country and Flag */}
      <div
        className={`flex w-[10%] cursor-pointer items-center justify-start ${getTextColorClass('country')}`}
        onClick={() => handleSort('country')}
      >
        <div className='flex items-center justify-start gap-x-2 text-xs'>
          Country <FontAwesomeIcon icon={getIcon('country')} />
        </div>
      </div>
      {/* Age */}
      <div
        className={`flex w-[10%] cursor-pointer items-center justify-center gap-x-2 text-xs ${getTextColorClass('age')}`}
        onClick={() => handleSort('age')}
      >
        Age <FontAwesomeIcon icon={getIcon('age')} />
      </div>
      {/* Played */}
      <div
        className={`flex w-[15%] cursor-pointer items-center justify-center gap-x-2 text-xs ${getTextColorClass('played')}`}
        onClick={() => handleSort('played')}
      >
        Events Played <FontAwesomeIcon icon={getIcon('played')} />
      </div>
      {/* Score */}
      <Tooltip placement='bottom'>
        <TooltipTrigger asChild>
          <div
            className={`flex w-[15%] cursor-pointer items-center justify-center gap-x-2 text-xs ${getTextColorClass('points')}`}
            onClick={() => handleSort('points')}
          >
            Points <FontAwesomeIcon icon={getIcon('points')} />
          </div>
        </TooltipTrigger>
        <TooltipContent className='w-52 rounded bg-black p-2 text-center text-sm text-white'>
          Official PPA points as of last PPA weekly ranking total.
        </TooltipContent>
      </Tooltip>
    </div>
  ) : (
    <div className='container flex w-full items-center p-0 py-3'>
      {/* Live Rank */}
      <Tooltip placement='bottom'>
        <TooltipTrigger asChild>
          <div
            className={clsx(
              'flex basis-[10%] cursor-pointer items-center justify-start gap-x-2 text-xs',
              getTextColorClass('rank')
            )}
            onClick={() => handleSort('rank')}
          >
            <span className='flex w-[46%]'>Live Rank</span>{' '}
            <FontAwesomeIcon icon={getIcon('rank')} />
          </div>
        </TooltipTrigger>
        <TooltipContent className='w-52 rounded bg-black p-2 text-center text-sm text-white'>
          Player&apos;s real-time ranking based on points earned/dropped in current week.
        </TooltipContent>
      </Tooltip>
      {/* Player */}
      <div
        className={`w-[23%] cursor-pointer ${getTextColorClass('player')}`}
        onClick={() => handleSort('player')}
      >
        <div className='flex items-center justify-start gap-x-2 text-xs'>
          Player <FontAwesomeIcon icon={getIcon('player')} />
        </div>
      </div>
      {/* Country and Flag */}
      <div
        className={`flex w-1/12 cursor-pointer items-center justify-start ${getTextColorClass('country')}`}
        onClick={() => handleSort('country')}
      >
        <div className='flex items-center justify-start gap-x-2 text-xs'>
          Country <FontAwesomeIcon icon={getIcon('country')} />
        </div>
      </div>
      {/* Age */}
      <div
        className={`flex w-1/12 cursor-pointer items-center justify-center gap-x-2 text-xs ${getTextColorClass('age')}`}
        onClick={() => handleSort('age')}
      >
        Age <FontAwesomeIcon icon={getIcon('age')} />
      </div>
      {/* Current Tournament */}
      <div
        className={`flex w-2/12 cursor-pointer items-center justify-center gap-x-2 text-xs ${getTextColorClass('currentTournament')}`}
        onClick={() => handleSort('currentTournament')}
      >
        Current Tournament <FontAwesomeIcon icon={getIcon('currentTournament')} />
      </div>
      {/* Live Points */}
      <Tooltip placement='bottom'>
        <TooltipTrigger asChild>
          <div
            className={`flex w-[10%] cursor-pointer items-center justify-center gap-x-2 text-xs ${getTextColorClass('livePoints')}`}
            onClick={() => handleSort('livePoints')}
          >
            <span className='flex w-[34%]'>Live Points</span>{' '}
            <FontAwesomeIcon icon={getIcon('livePoints')} />
          </div>
        </TooltipTrigger>
        <TooltipContent className='w-52 rounded bg-black p-2 text-center text-sm text-white'>
          Player&apos;s real-time points total reflecting points earned/dropped in current week.
        </TooltipContent>
      </Tooltip>
      {/* Official Points */}
      <Tooltip placement='bottom'>
        <TooltipTrigger asChild>
          <div
            className={`flex w-[10%] cursor-pointer items-center justify-center gap-x-2 text-xs ${getTextColorClass('officialPoints')}`}
            onClick={() => handleSort('officialPoints')}
          >
            <span className='flex w-[39%]'>Official Points</span>{' '}
            <FontAwesomeIcon icon={getIcon('officialPoints')} />
          </div>
        </TooltipTrigger>
        <TooltipContent className='w-52 rounded bg-black p-2 text-center text-sm text-white'>
          Official PPA points as of last PPA weekly ranking total.
        </TooltipContent>
      </Tooltip>
      {/* +/- */}
      <Tooltip placement='bottom'>
        <TooltipTrigger asChild>
          <div
            className={`w-w-[6%] flex cursor-pointer items-center justify-end gap-x-2 text-xs ${getTextColorClass('plusMinus')}`}
            onClick={() => handleSort('plusMinus')}
          >
            + / - <FontAwesomeIcon icon={getIcon('plusMinus')} />
          </div>
        </TooltipTrigger>
        <TooltipContent className='w-52 rounded bg-black p-2 text-center text-sm text-white'>
          Difference in points earned/dropped in current week compared to most recent PPA weekly
          points total.
        </TooltipContent>
      </Tooltip>
      {/* Next/Max Points */}
      <Tooltip placement='bottom'>
        <TooltipTrigger asChild>
          <div
            className={`w-w-[10%] flex cursor-pointer items-center justify-end gap-x-2 text-xs ${getTextColorClass('nextMaxPoints')}`}
            onClick={() => handleSort('nextMaxPoints')}
          >
            <span className='flex w-[54%]'>Next/Max Points</span>{' '}
            <FontAwesomeIcon icon={getIcon('nextMaxPoints')} />
          </div>
        </TooltipTrigger>
        <TooltipContent className='w-52 rounded bg-black p-2 text-center text-sm text-white'>
          Next available points earned if player wins next match. Max points potential if player
          wins tournament.
        </TooltipContent>
      </Tooltip>
    </div>
  );
};
